<script setup lang="ts">

import { ref, onMounted } from 'vue';
import { useBankingStore } from '@/banking/store/bankingStore.ts';
import { storeToRefs } from 'pinia';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import Skeleton from '@/application/components/ui/Skeleton.vue';
import { ElAlert } from 'element-plus';
import { NotificationVariant, useNotification } from '@/application/components/ui/useNotification';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
import { captureException, captureMessage } from '@sentry/vue';
import BankingInfoModal from '@/banking/components/BankingInfoModal.vue';
import { removeLocalStorage } from '@/application/utils/localStorage.ts';
import { ElButton } from 'element-plus';
import { throwableCall } from '@/api/lib/integration.ts';
import { createBanksApiUser } from '@/api/banking/banksApi.ts';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events.ts';
import SvgVue from '@/application/components/SvgVue.ts';

const LS_KEY = 'first-visit-banking';

const baseDataStore = useBaseDataStore();
const { isBroker, isMagicLogin, isBanksApiUser } = storeToRefs(baseDataStore);
const bankingStore = useBankingStore();
const { token, error, loading } = storeToRefs(bankingStore);
const { show: showNotification } = useNotification();

const isCreatingBankingUser = ref(false);
const showBankingInfoModal = ref(!isBanksApiUser.value);

async function onEmitError(e: CustomEvent, componentName: string) {
  const { status, statusText } = e.detail;
  captureMessage('Web Component Error', {
    level: 'warning',
    tags: {
      "status": status ,
      "statusText": statusText ,
      "webComponent": componentName,
    },
  });
  showNotification('Es gab einen Fehler beim Abrufen der Banking Daten.', NotificationVariant.Error);
}

async function createBankingUser() {
  try {
    isCreatingBankingUser.value = true;
    await throwableCall(createBanksApiUser())
    useTagManager().push({ event: TrackEventCategory.BANKS_API, action: 'createUser' });
    await bankingStore.fetchToken();
    await baseDataStore.fetchBaseData();
  } catch (err) {
    captureException(err);
    showNotification('Es gab einen Fehler beim Erstellen.', NotificationVariant.Error);
  } finally {
    isCreatingBankingUser.value = false;
  }
}

onMounted(async () => {
  removeLocalStorage(LS_KEY);
  if (isBanksApiUser.value && token.value.length === 0) await bankingStore.fetchToken()
})
</script>

<template>
  <div class="banking-theme md:container flex flex-col grow md:px-4">
    <ElButton size="small" class="z-[999] mt-4 mr-4 -mb-12 ml-auto" circle bg @click="showBankingInfoModal = true">
      <FaIcon name="question" size="lg" variant="regular" />
    </ElButton>
    <Skeleton v-if="loading" class="h-full w-full" />
    <ElAlert
      v-else-if="error"
      class="my-auto mx-5 md:mx-auto"
      type="error"
      show-icon
    >
      {{ error }}
    </ElAlert>
    <div
      v-else-if="token.length > 0 && !isBroker && !isMagicLogin"
      class="relative h-full"
    >
      <slot :token="token" :on-emit-error="onEmitError" />
    </div>
    <div
      v-if="!isBanksApiUser"
      class="flex flex-col items-center justify-center h-full gap-10 py-6"
    >
      <SvgVue icon="banking/multibanking" />
      <div class="flex flex-col md:flex-row justify-center items-center gap-3">
        <ElButton
          type="primary"
          size="large"
          plain
          @click="showBankingInfoModal = true"
        >
          Multibanking Infos
        </ElButton>
        <ElButton
          type="primary"
          :loading="isCreatingBankingUser"
          size="large"
          @click="createBankingUser"
        >
          Multibanking ausprobieren
        </ElButton>
      </div>
    </div>
  </div>
  <BankingInfoModal
    v-model:show="showBankingInfoModal"
    @set-up="createBankingUser"
  />
</template>

<style>
:root {
  /** DEMV CSS Variables **/
  /** primary **/
  --ba-color-primary: #4181d2;
  --ba-color-primary-rgb: 65,129,210;
  --ba-color-primary-contrast: #ffffff;
  --ba-color-primary-contrast-rgb: 0,0,0;
  --ba-color-primary-shade: #3972b9;
  --ba-color-primary-tint: #548ed7;
  /** secondary **/
  --ba-color-secondary: #2360b0;
  --ba-color-secondary-rgb: 35,96,176;
  --ba-color-secondary-contrast: #ffffff;
  --ba-color-secondary-contrast-rgb: 255,255,255;
  --ba-color-secondary-shade: #1f549b;
  --ba-color-secondary-tint: #3970b8;
  /** tertiary **/
  --ba-color-tertiary: #b4ceef;
  --ba-color-tertiary-rgb: 180,206,239;
  --ba-color-tertiary-contrast: #000000;
  --ba-color-tertiary-contrast-rgb: 0,0,0;
  --ba-color-tertiary-shade: #9eb5d2;
  --ba-color-tertiary-tint: #bcd3f1;
  /** success **/
  --ba-color-success: #5a7d1a;
  --ba-color-success-rgb: 90,125,26;
  --ba-color-success-contrast: #ffffff;
  --ba-color-success-contrast-rgb: 255,255,255;
  --ba-color-success-shade: #4f6e17;
  --ba-color-success-tint: #6b8a31;
  /** warning **/
  --ba-color-warning: #b35900;
  --ba-color-warning-rgb: 179,89,0;
  --ba-color-warning-contrast: #ffffff;
  --ba-color-warning-contrast-rgb: 255,255,255;
  --ba-color-warning-shade: #9e4e00;
  --ba-color-warning-tint: #bb6a1a;
  /** danger **/
  --ba-color-danger: #9e2511;
  --ba-color-danger-rgb: 158,37,17;
  --ba-color-danger-contrast: #ffffff;
  --ba-color-danger-contrast-rgb: 255,255,255;
  --ba-color-danger-shade: #8b210f;
  --ba-color-danger-tint: #a83b29;
  /** medium **/
  --ba-color-medium: #6d727d;
  --ba-color-medium-rgb: 109,114,125;
  --ba-color-medium-contrast: #ffffff;
  --ba-color-medium-contrast-rgb: 255,255,255;
  --ba-color-medium-shade: #60646e;
  --ba-color-medium-tint: #7c808a;
  /** core **/
  --ba-background-color: #fafaff;
  --ba-background-color-rgb: 250,250,255;
  --ba-text-color: #292b31;
  --ba-text-color-rgb: 41,43,49;
  --ba-font-family: 'Inter var', "Titillium Web", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  /** custom settings for theming **/
  --ba-border-radius-default: 10px;
  --ba-box-bg-color: #fff;
  --ba-link-color: var(--ba-color-secondary);
  --ba-link-hover-color: var(--ba-color-secondary-shade);
  --ba-link-decoration: none;
  --ba-link-hover-decoration: underline;
  --ba-icon-color: var(--ba-color-secondary);
  --ba-detail-icon-color: var(--ba-color-medium);
  --ba-btn-border-radius: 4px;
  --ba-input-border-color: #858b98;
  --ba-progress-bar-bg-color: var(--ba-color-tertiary);
  --ba-progress-bar-progress-color: var(--ba-color-secondary);
  --ba-progress-bar-border-color: var(--ba-color-secondary);
}
</style>
